/**
 * Read-only "Evidence feedback stats" — per-statement judged / relevant /
 * precision plus the top rejection reasons, from
 * `GET /api/admin/evidence-feedback/stats?level=acc|pcc|mcc`.
 *
 * The endpoint ships with the B1 backend slice; a 404 renders a
 * "not deployed yet" note instead of an error so the grading panel keeps working.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { ActivityIndicator, TouchableOpacity, View } from 'react-native';
import { MaterialIcons } from '@expo/vector-icons';

import { Text } from '@/components/ui/text';
import { apiRequest } from '../../services/apiClient';
import { AdminCalloutBanner, AdminStatusPill, ADMIN_COLORS } from '@/components/admin/AdminUI';

type StatsLevel = 'acc' | 'pcc' | 'mcc';
const LEVELS: StatsLevel[] = ['acc', 'pcc', 'mcc'];

interface StatementStat {
  statement_id: string;
  judged: number;
  relevant: number;
  /** 0–1 share of judged citations marked relevant; null when nothing judged. */
  precision: number | null;
  top_reasons: { reason: string; count: number }[];
}

interface EvidenceFeedbackStats {
  level: StatsLevel;
  total_judged: number;
  total_relevant: number;
  precision: number | null;
  statements: StatementStat[];
}

type RawStatement = Partial<StatementStat> & { id?: string; reasons?: Record<string, number> | { reason: string; count: number }[] };
type RawStats = Partial<Omit<EvidenceFeedbackStats, 'statements'>> & {
  statements?: RawStatement[] | Record<string, RawStatement>;
};

/** Tolerates array-or-record statements and record-or-array reasons. */
function normalize(level: StatsLevel, raw: RawStats): EvidenceFeedbackStats {
  const entries: RawStatement[] = Array.isArray(raw.statements)
    ? raw.statements
    : Object.entries(raw.statements || {}).map(([id, item]) => ({ statement_id: id, ...item }));
  const statements = entries.map((item) => {
    const judged = Number(item.judged ?? 0);
    const relevant = Number(item.relevant ?? 0);
    const reasonsRaw = item.top_reasons ?? item.reasons ?? [];
    const top_reasons = Array.isArray(reasonsRaw)
      ? reasonsRaw
      : Object.entries(reasonsRaw).map(([reason, count]) => ({ reason, count: Number(count) }));
    return {
      statement_id: String(item.statement_id ?? item.id ?? '?'),
      judged,
      relevant,
      precision: typeof item.precision === 'number' ? item.precision : judged > 0 ? relevant / judged : null,
      top_reasons: [...top_reasons].sort((a, b) => b.count - a.count).slice(0, 3),
    };
  });
  const totalJudged = Number(raw.total_judged ?? statements.reduce((sum, s) => sum + s.judged, 0));
  const totalRelevant = Number(raw.total_relevant ?? statements.reduce((sum, s) => sum + s.relevant, 0));
  return {
    level,
    total_judged: totalJudged,
    total_relevant: totalRelevant,
    precision: typeof raw.precision === 'number' ? raw.precision : totalJudged > 0 ? totalRelevant / totalJudged : null,
    statements: statements.sort((a, b) => b.judged - a.judged),
  };
}

const pct = (value: number | null) => (value === null ? '—' : `${Math.round(value * 100)}%`);

export default function EvidenceFeedbackStatsPanel() {
  const [level, setLevel] = useState<StatsLevel>('acc');
  const [stats, setStats] = useState<EvidenceFeedbackStats | null>(null);
  const [state, setState] = useState<'loading' | 'ready' | 'not_deployed' | 'error'>('loading');

  const load = useCallback(async (nextLevel: StatsLevel) => {
    setState('loading');
    try {
      const raw = await apiRequest<RawStats>({
        endpoint: `/admin/evidence-feedback/stats?level=${nextLevel}`,
        method: 'GET',
      });
      setStats(normalize(nextLevel, raw || {}));
      setState('ready');
    } catch (error) {
      // `apiRequest` attaches `.status`; axios-style errors carry `.response.status`.
      const shaped = error as { status?: number; response?: { status?: number } };
      const status = shaped?.status ?? shaped?.response?.status;
      setStats(null);
      setState(status === 404 ? 'not_deployed' : 'error');
    }
  }, []);

  useEffect(() => { void load(level); }, [level, load]);

  return (
    <View
      testID="evidence_feedback_stats"
      style={{ borderWidth: 1, borderColor: ADMIN_COLORS.border, borderRadius: 14, backgroundColor: ADMIN_COLORS.surface, padding: 14, gap: 12 }}
    >
      <View className="flex-row items-center justify-between" style={{ gap: 10, flexWrap: 'wrap' }}>
        <View className="flex-row items-center" style={{ gap: 8 }}>
          <Text className="text-sm font-bold" style={{ color: ADMIN_COLORS.text.primary }}>Evidence feedback stats</Text>
          <AdminStatusPill label="Read-only" tone="primary" />
        </View>
        <View className="flex-row" style={{ gap: 6 }}>
          {LEVELS.map((value) => {
            const selected = value === level;
            return (
              <TouchableOpacity
                key={value}
                testID={`evidence_feedback_stats_level_${value}`}
                onPress={() => setLevel(value)}
                accessibilityRole="button"
                accessibilityState={{ selected }}
                style={{
                  paddingVertical: 6, paddingHorizontal: 12, borderRadius: 999, borderWidth: 1,
                  borderColor: selected ? ADMIN_COLORS.primary : ADMIN_COLORS.border,
                  backgroundColor: selected ? ADMIN_COLORS.primarySoft : ADMIN_COLORS.background,
                }}
              >
                <Text className="text-xs font-bold" style={{ color: selected ? ADMIN_COLORS.primary : ADMIN_COLORS.text.secondary }}>
                  {value.toUpperCase()}
                </Text>
              </TouchableOpacity>
            );
          })}
          <TouchableOpacity onPress={() => void load(level)} accessibilityRole="button" accessibilityLabel="Refresh" style={{ padding: 6 }}>
            <MaterialIcons name="refresh" size={18} color={ADMIN_COLORS.text.secondary} />
          </TouchableOpacity>
        </View>
      </View>
      <Text className="text-sm" style={{ color: ADMIN_COLORS.text.secondary }}>
        How often mentors keep the citations the instrument attached to each statement. Precision = relevant ÷ judged.
      </Text>

      {state === 'loading' ? (
        <View className="items-center py-4"><ActivityIndicator color={ADMIN_COLORS.primary} /></View>
      ) : state === 'not_deployed' ? (
        <AdminCalloutBanner tone="info" body="Stats endpoint is not deployed on this environment yet (GET /api/admin/evidence-feedback/stats returned 404)." />
      ) : state === 'error' || !stats ? (
        <AdminCalloutBanner tone="warning" body="Could not load evidence feedback stats. Use Refresh to try again." />
      ) : (
        <View style={{ gap: 10 }}>
          <View className="flex-row flex-wrap" style={{ gap: 10 }}>
            {[
              ['Judged', String(stats.total_judged)],
              ['Relevant', String(stats.total_relevant)],
              ['Precision', pct(stats.precision)],
              ['Statements with data', String(stats.statements.filter((s) => s.judged > 0).length)],
            ].map(([label, value]) => (
              <View key={label} style={{ minWidth: 120, gap: 2 }}>
                <Text className="text-xs" style={{ color: ADMIN_COLORS.text.muted }}>{label}</Text>
                <Text className="text-sm font-bold" style={{ color: ADMIN_COLORS.text.primary }}>{value}</Text>
              </View>
            ))}
          </View>
          {stats.statements.length === 0 ? (
            <Text className="text-sm" style={{ color: ADMIN_COLORS.text.muted }}>No relevance judgements recorded for this level yet.</Text>
          ) : (
            <View style={{ gap: 6 }}>
              {stats.statements.map((row) => (
                <View
                  key={row.statement_id}
                  testID={`evidence_feedback_stat_${row.statement_id}`}
                  className="flex-row items-start"
                  style={{ gap: 10, paddingVertical: 6, borderTopWidth: 1, borderTopColor: ADMIN_COLORS.border }}
                >
                  <Text className="text-xs font-bold" style={{ width: 56, color: ADMIN_COLORS.text.primary }}>{row.statement_id}</Text>
                  <Text className="text-xs" style={{ width: 150, color: ADMIN_COLORS.text.secondary }}>
                    {`${row.relevant}/${row.judged} relevant · ${pct(row.precision)}`}
                  </Text>
                  <Text className="text-xs" style={{ flex: 1, color: ADMIN_COLORS.text.muted }}>
                    {row.top_reasons.length
                      ? row.top_reasons.map((r) => `${r.reason} ×${r.count}`).join(' · ')
                      : '—'}
                  </Text>
                </View>
              ))}
            </View>
          )}
        </View>
      )}
    </View>
  );
}
